import {useEffect} from 'react';
import styled from 'styled-components';
import {ComboBox, Text} from '@forecast-it/design-system';
import {useForecastProjects, type ForecastProjectOption} from '../hooks/useForecastProjects';

interface ProjectDropdownProps {
	value: string | null;
	onChange: (projectId: string | null) => void;
	/** Visible label above the field; it is also the input's accessible name. */
	label?: string;
	disabled?: boolean;
	/**
	 * For an optional field: adds a first entry with this label that stands for "no
	 * project", so the user can clear a selection by picking it. It reports null.
	 */
	noneLabel?: string;
	'data-cy'?: string;
}

/** Key of the `noneLabel` entry; the combobox needs a key for the cleared state. */
const NO_PROJECT = '__no_project__';

const Hint = styled.div`
	margin-top: 4px;
`;

/**
 * Type-to-filter project picker for ticket flows. The list comes from
 * useForecastProjects, the same source as the Add task modal's Project field, so
 * its contents, labels and order match that field. It opens to the full list on
 * focus, and typing filters it.
 */
export function ProjectDropdown({
	value,
	onChange,
	label = 'Project',
	disabled = false,
	noneLabel,
	'data-cy': dataCy = 'project-dropdown',
}: ProjectDropdownProps) {
	const {projects, loading, error} = useForecastProjects();

	// A value the viewer cannot pick (e.g. a default passed in from a project screen
	// that is not among their projects) would otherwise sit unseen behind an empty
	// input and still be submitted. Drop it once the list has loaded.
	const valueUnknown = value != null && !loading && !error && !projects.some(project => project.id === value);
	useEffect(() => {
		if (valueUnknown) onChange(null);
	}, [valueUnknown, onChange]);

	const items: ForecastProjectOption[] = noneLabel ? [{id: NO_PROJECT, name: noneLabel}, ...projects] : projects;

	return (
		<>
			{/* The design-system ComboBox takes its selection through `value` and reports
			    it through `onChange`; it maps those onto react-aria's selectedKey /
			    onSelectionChange itself, so passing those names directly clears the
			    selection. `disabled` only styles the input; `isDisabled` also locks the
			    button that opens the list. */}
			<ComboBox
				data-cy={dataCy}
				label={label}
				width="flex"
				menuTrigger="focus"
				placeholder={loading ? 'Loading projects…' : 'Select a project…'}
				value={value ?? (noneLabel ? NO_PROJECT : null)}
				defaultItems={items}
				disabled={disabled || loading}
				isDisabled={disabled || loading}
				onChange={(key: string | null) => onChange(key && key !== NO_PROJECT ? key : null)}
			>
				{(item: ForecastProjectOption) => (
					<ComboBox.Item key={item.id} textValue={item.name}>
						<Text noWrap>{item.name}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>
			{!loading && error && (
				<Hint>
					<Text>Couldn't load projects.</Text>
				</Hint>
			)}
			{!loading && !error && projects.length === 0 && (
				<Hint>
					<Text>No projects available.</Text>
				</Hint>
			)}
		</>
	);
}
